import {
  applyPromotionDiscount,
  computeGrossPrice,
  formatTaxHint,
  getActivePromotion,
  getProductCategoryLabel,
  getProductCategoryLabels,
  getPriceRange,
  getPromotionHintText,
  toCurrency,
} from "./shopUtils";

const ORDER_DATE_FORMATTER = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const INVOICE_DOWNLOADABLE_ORDER_STATUSES = new Set([
  "fulfilled",
  "exportedSepa",
]);

export const MEMBER_CATALOG_LAYOUT_STORAGE_KEY =
  "ma-shop-member-catalog-layout";

export const getInitialMemberCatalogLayout = () => {
  try {
    const storedValue = localStorage.getItem(MEMBER_CATALOG_LAYOUT_STORAGE_KEY);
    if (storedValue === "list" || storedValue === "cards") return storedValue;
  } catch {
    // ignore storage access problems
  }

  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
  ) {
    return window.matchMedia("(max-width:599.95px)").matches ? "list" : "cards";
  }

  return "list";
};

export const formatOrderDate = (value) => {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "Unbekanntes Datum";
  return ORDER_DATE_FORMATTER.format(date);
};

export const buildCompactOrderSummary = (lineItems = []) => {
  if (!Array.isArray(lineItems) || !lineItems.length)
    return "Keine Positionen vorhanden";

  return lineItems
    .slice(0, 3)
    .map((lineItem) => {
      const variantLabel = String(lineItem?.variantLabel || "Standard").trim();
      const variantPart =
        variantLabel && variantLabel !== "Standard" ? ` (${variantLabel})` : "";

      return `${lineItem.productName}${variantPart} ×${lineItem.quantity}`;
    })
    .join(", ");
};

export const mapOrderLineItemsToCartItems = (lineItems = []) => {
  const grouped = new Map();

  (lineItems || []).forEach((lineItem) => {
    const productId = String(lineItem?.productId || "").trim();
    const variantKey = String(lineItem?.variantKey || "").trim();
    const quantity = Number(lineItem?.quantity || 0);

    if (
      !productId ||
      !variantKey ||
      !Number.isInteger(quantity) ||
      quantity < 1
    )
      return;

    const key = `${productId}::${variantKey}`;
    const current = grouped.get(key) || {
      productId,
      variantKey,
      productName:
        String(lineItem?.productName || "Produkt").trim() || "Produkt",
      variantLabel:
        String(lineItem?.variantLabel || "Standard").trim() || "Standard",
      quantity: 0,
      unitPrice: Number(lineItem?.unitPrice || 0),
      availableStock: null,
      isInStock: true,
    };

    current.quantity += quantity;
    grouped.set(key, current);
  });

  return Array.from(grouped.values());
};

export const buildProductViewModel = (product, referenceDate) => {
  const hasVariants = (product?.optionSets?.length || 0) > 0;
  const promotion = getActivePromotion(product, referenceDate);
  const hasPromotion = Boolean(promotion);
  const originalPriceRange = getPriceRange(product);
  const discountedPriceRange = hasPromotion
    ? getPriceRange(product, { promotion, referenceDate })
    : originalPriceRange;
  const showOriginalRange =
    hasVariants && originalPriceRange.min !== originalPriceRange.max;
  const showDiscountedRange =
    hasVariants && discountedPriceRange.min !== discountedPriceRange.max;
  const productTaxRate = Number(product?.taxRate || 0);
  const taxHint = formatTaxHint(productTaxRate);
  const originalPriceText = showOriginalRange
    ? `${toCurrency(originalPriceRange.min)} - ${toCurrency(
        originalPriceRange.max
      )}`
    : toCurrency(
        hasVariants
          ? originalPriceRange.min
          : computeGrossPrice(product?.basePrice, productTaxRate)
      );
  const discountedPriceText = showDiscountedRange
    ? `${toCurrency(discountedPriceRange.min)} - ${toCurrency(
        discountedPriceRange.max
      )}`
    : toCurrency(
        hasVariants
          ? discountedPriceRange.min
          : computeGrossPrice(
              applyPromotionDiscount(product?.basePrice, promotion),
              productTaxRate
            )
      );

  return {
    hasPromotion,
    hasVariants,
    promotion,
    categories: getProductCategoryLabels(product),
    category: getProductCategoryLabel(product),
    promotionHintText: hasPromotion
      ? getPromotionHintText(promotion, referenceDate)
      : "",
    discountedPriceText,
    originalPriceText,
    taxHint,
    description: String(product?.description || "").trim(),
  };
};
